import { useState, useEffect } from 'react';
import { getNews } from '../lib/alpaca';

const TOP_SYMBOLS = ['AAPL', 'MSFT', 'NVDA', 'TSLA', 'AMZN', 'GOOGL', 'META', 'VOO', 'QQQ', 'SPY'];

function timeAgo(dateStr) {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now - date;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 60) return `hace ${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `hace ${hours}h`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'ayer';
  return `hace ${days}d`;
}

function getSymbolColor(symbol) {
  const colors = {
    AAPL: '#555555', MSFT: '#00A4EF', NVDA: '#76B900', TSLA: '#CC0000',
    AMZN: '#FF9900', GOOGL: '#4285F4', META: '#0081FB', VOO: '#820000',
    QQQ: '#00A3E0', SPY: '#00704A',
  };
  return colors[symbol] || '#2A59C6';
}

export default function NewsFeed({ onSymbolClick }) {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const articles = await getNews(TOP_SYMBOLS, 20);
        if (!cancelled) {
          // Deduplicate by headline
          const seen = new Set();
          const unique = articles.filter((a) => {
            if (seen.has(a.headline)) return false;
            seen.add(a.headline);
            return true;
          });
          setNews(unique);
        }
      } catch (e) {
        console.error('news feed:', e);
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5">
        <div className="flex items-center gap-2 mb-4">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2A59C6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 22h16a2 2 0 002-2V4a2 2 0 00-2-2H8a2 2 0 00-2 2v16a2 2 0 01-2 2zm0 0a2 2 0 01-2-2v-9c0-1.1.9-2 2-2h2"/><path d="M18 14h-8"/><path d="M15 18h-5"/><path d="M10 6h8v4h-8V6z"/></svg>
          <span className="text-sm font-semibold text-[#181D27]">Noticias del mercado</span>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse">
              <div className="h-3 bg-[#F3F4F6] rounded w-1/4 mb-2" />
              <div className="h-4 bg-[#F3F4F6] rounded w-3/4 mb-1" />
              <div className="h-3 bg-[#F3F4F6] rounded w-full" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error || news.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5 text-center">
        <div className="text-sm text-[#9CA3AF]">Noticias no disponibles en este momento</div>
      </div>
    );
  }

  const visibleNews = expanded ? news : news.slice(0, 5);

  return (
    <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden">
      <div className="px-5 pt-5 pb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2A59C6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 22h16a2 2 0 002-2V4a2 2 0 00-2-2H8a2 2 0 00-2 2v16a2 2 0 01-2 2zm0 0a2 2 0 01-2-2v-9c0-1.1.9-2 2-2h2"/><path d="M18 14h-8"/><path d="M15 18h-5"/><path d="M10 6h8v4h-8V6z"/></svg>
          <span className="text-sm font-semibold text-[#181D27]">Noticias del mercado</span>
        </div>
        <span className="text-xs text-[#9CA3AF]">{news.length} artículos</span>
      </div>

      <div className="divide-y divide-[#F3F4F6]">
        {visibleNews.map((article) => (
          <div key={article.id} className="px-5 py-3.5 hover:bg-[#FAFAFA] transition">
            <div className="flex items-start gap-3">
              {article.images?.[0]?.url && (
                <img
                  src={article.images[0].url}
                  alt=""
                  className="w-16 h-16 rounded-lg object-cover flex-shrink-0 mt-0.5"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] font-semibold text-[#6B7280] uppercase">{article.source}</span>
                  <span className="text-[10px] text-[#9CA3AF]">{timeAgo(article.created_at)}</span>
                  {article.symbols && article.symbols.length > 0 && (
                    <div className="flex gap-1 ml-auto">
                      {article.symbols.slice(0, 3).map((sym) => (
                        <button
                          key={sym}
                          onClick={(e) => { e.stopPropagation(); onSymbolClick?.(sym); }}
                          className="px-1.5 py-0.5 rounded text-[9px] font-bold hover:opacity-80 transition"
                          style={{ backgroundColor: getSymbolColor(sym) + '15', color: getSymbolColor(sym) }}
                        >
                          {sym}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <a
                  href={article.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-semibold text-[#181D27] hover:text-[#2A59C6] transition line-clamp-2 block mb-1"
                >
                  {article.headline}
                </a>
                <p className="text-xs text-[#6B7280] line-clamp-2 leading-relaxed">{article.summary}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {news.length > 5 && (
        <div className="px-5 py-3 border-t border-[#F3F4F6] text-center">
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-xs font-semibold text-[#2A59C6] hover:underline"
          >
            {expanded ? 'Ver menos' : `Ver todas (${news.length})`}
          </button>
        </div>
      )}
    </div>
  );
}
